import * as React from "react";
import ReactTooltip from 'react-tooltip';

import "./confirm-tab.scss";

import {cst, cstAssign}             from "@Common/react/chain-services";
import {consumeContext}             from "@Common/react/context";
import {scopedState}                from "@Common/react/scoped-state";
import {UseState}                   from "@Common/react/use-state";
import {Types}                      from "@Common/types";
import {Responsive}                 from "@Common/ui-components/responsive";
import {oMapToArrWithIndex}         from "@Common/utils/objects";

import {ModalLayout}                from "@Xpresso/routes/common/modal-layout/modal-layout";

import {UserCreateTabs,
        goToPreviousTab,
        USER_TAB}            from "../../user-create-tabs";

import {getAgentFields}             from "../general-tab/general-tab";

interface VarStack {
    apis          : Types.Apis.Loaders.XpressoLoadApis,
    company       : any,
    userLevels    : Types.Apis.Loaders.LoadUserLevelsNext,
    uploading     : Types.Common.React.UseState.UseStateNext<boolean>,
    agent         : any,
    routerHistory : any,
    responsive    : any,
}

export const ConfirmTab = ({step, agent, reload}) : ({
    step: Types.Common.React.UseState.UseStateNext<number>,
    modal: Types.Common.UiComponents.ModalService3.ModalService3RootNext<void>,
    agent: Types.Common.React.UseState.UseStateNext<string>,
}) => cst<VarStack>(
    consumeContext("apis"),
    consumeContext("company"),
    consumeContext("userLevels"),
    consumeContext("routerHistory"),
    cstAssign("uploading", (_, next) => UseState({next})),
    cstAssign("agent", (_,next) => next(agent.value.inputs)),
    cstAssign("responsive", (_, next) => <Responsive {...{
        sizes: {
            md: 1024,
        },
        next,
    }}/>),
    ({apis, uploading, responsive, routerHistory},next) => (
        ModalLayout({
            title: "Create New User",
            content: (
                <>
                    {responsive.ge("md") && UserCreateTabs({step})}
                    {next()}
                </>
            ),
            buttons: [
                {
                    label: "Back",
                    onClick: () => goToPreviousTab(step),
                },
                {
                    label: "Create User",
                    primary: true,
                    disabled: uploading.value,
                    onClick: async () => {
                        uploading.onChange(true);
                        const createFields = {...agent.value.inputs};
                        delete createFields.matchPassword
                        const result = await apis.next.session.createAgent({createFields});
                        if (!result?.statusCode || result?.statusCode !== "success") {
                            if(result?.data) {
                                const errors = JSON.parse(result.data);
                                errors.map((error) => {
                                    scopedState(agent, ['errors', error.mapping]).onChange(error.message)
                                });
                                if(errors.some((error) => getAgentFields().map(a => a.mapping).includes(error.mapping))) {
                                    step.onChange(USER_TAB.GENERAL);
                                } else {
                                    step.onChange(USER_TAB.LOGIN);
                                }
                            } else {
                                alert(result.message);
                            }
                        } else {
                            step.onChange(USER_TAB.GENERAL);
                            agent.onChange({
                                inputs: {},
                                errors: {},
                            });
                            reload();
                            window.opener?.postMessage?.({
                                type: "agentCreated",
                                payload: {...agent.value.inputs, idAgent: result.data.idAgent},
                            });
                            routerHistory.replace('/account/admin/user');
                        }
                        uploading.onChange(false);
                    },
                },
            ]
        })
    ),
    ({company, agent}) => (
        <div className="confirm-tab-j7d">
            <div className="section">
                <div className="header">
                    <div className="title">
                        General Information
                    </div>
                    <a className="edit" xd-qa-type="link" xd-qa-name="Edit General Information" onClick={() => step.onChange(USER_TAB.GENERAL)}>
                        Edit
                    </a>
                </div>
                <div className="card">
                    <div className="name" xd-qa-type="label" xd-qa-name="Agent Name">
                        {getAgentFullName(agent)}
                    </div>
                    <div className="email" xd-qa-type="label" xd-qa-name="Agent Email">
                        {agent.agentEmail}
                    </div>
                    <div className="phone-numbers">
                        <div className="phone">
                            <div className="label">
                                Phone 1
                            </div>
                            <div className="number" xd-qa-type="label" xd-qa-name="Agent Phone 1" >
                                {agent.agentPhone1}
                            </div>
                        </div>
                        <div className="phone">
                            <div className="label">
                                Phone 2
                            </div>
                            <div className="number" xd-qa-type="label" xd-qa-name="Agent Phone 2" >
                                {agent.agentPhone2}
                            </div>
                        </div>
                    </div>
                    {agent.agentLinkId && <div className="link-id">
                        <div className="label">
                            Link ID
                        </div>
                        <div className="value" xd-qa-type="label" xd-qa-name="Link ID" >
                            {agent.agentLinkId}
                        </div>
                    </div>}
                </div>
            </div>
            <div className="section">
                <div className="header">
                    <div className="title">
                        Login Information
                    </div>
                    <a className="edit" xd-qa-type="link" xd-qa-name="Edit Login Information" onClick={() => step.onChange(USER_TAB.LOGIN)}>
                        Edit
                    </a>
                </div>
                <div className="card">
                    <div className="display">
                        <div className="label">
                            Username
                        </div>
                        <div className="value" xd-qa-type="label" xd-qa-name="Username" >
                            {agent.userName}
                        </div>
                    </div>
                </div>
            </div>
            <div className="section">
                <div className="header">
                    <div className="title">
                        Customization Information
                    </div>
                    <a className="edit" xd-qa-type="link" xd-qa-name="Edit Customization Information" onClick={() => step.onChange(USER_TAB.CUSTOMIZATION)}>
                        Edit
                    </a>
                </div>
                <div className="sub-title">
                    You may use this information on product customization
                </div>
                <div className="card">
                    {oMapToArrWithIndex(company.displayFields, (displayField, lineNumber, i) => (
                        <React.Fragment key={i}>
                            <div className="display">
                                <div className="label" xd-qa-type="label" xd-qa-name={"Agent Display Label " + i} >
                                    {displayField}
                                </div>
                                <div className="value" xd-qa-type="label" xd-qa-name={"Agent Display " + i} >
                                    {agent["agentDisplay" + lineNumber]}
                                </div>
                            </div>
                            {(i+1 < Object.keys(company.displayFields).length) && <div className="sub-divider"/>}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </div>
    )
);

const getAgentFullName = (agent) => {
    let name = [];
    agent.agentNameFirst && name.push(agent.agentNameFirst)
    agent.agentNameMiddle && name.push(agent.agentNameMiddle)
    agent.agentNameLast && name.push(agent.agentNameLast)
    return name.join(" ");
}
